/**
 * BoochBod cart drawer — upsell behaviour
 *
 * Handles the two offers added by snippets/bb-cart-upsell.liquid:
 *   - "Add an Extra Pack"    (flavour buttons -> add that variant)
 *   - "Add Product Protection" (checkbox -> add or remove that variant)
 *
 * WHY EVENT DELEGATION
 * The drawer replaces its own innerHTML on every refresh (see
 * refreshCartDrawer() in cart-progress-bar.js). Handlers bound directly to
 * buttons are destroyed by that. Delegating from `document` survives it —
 * this is the same approach CartSubscribeSwap already takes, and it is why
 * these controls keep working after the first add.
 *
 * WHY /cart/add.js AND NOT A PERMALINK
 * Carried over from the build doc: cart permalinks silently drop the selling
 * plan and create a one-time purchase, and because the plan carries a $0
 * discount the totals look identical, so the bug is invisible at checkout.
 * Nothing here should ever become an <a href>.
 */
(function () {
  'use strict';

  var busy = false;

  function setBusy(state) {
    busy = state;
    document
      .querySelectorAll('[data-bb-add-variant], [data-bb-protect-variant]')
      .forEach(function (el) {
        el.disabled = state;
      });
  }

  function refresh() {
    // Reuse the drawer's own refresh when it is present so we re-render
    // through exactly one code path. Reload is the honest fallback: a stale
    // drawer that disagrees with the real cart is worse than a reload.
    if (typeof window.refreshCartDrawer === 'function') {
      return Promise.resolve(window.refreshCartDrawer()).then(syncEmptyState);
    }
    window.location.reload();
    return Promise.resolve();
  }

  // refreshCartDrawer() swaps .drawer__inner but never toggles Dawn's
  // `is-empty` on <cart-drawer> (cart.js:177 does that only on Dawn's own
  // path), and `cart-drawer:not(.is-empty) .cart-drawer__warnings` hides
  // the empty-state content — hence a blank cream drawer. 2026-09-13.
  function syncEmptyState() {
    return fetch(window.Shopify.routes.root + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.classList.toggle('is-empty', cart.item_count === 0);
        var items = document.querySelector('cart-drawer-items');
        if (items) items.classList.toggle('is-empty', cart.item_count === 0);
      })
      .catch(function () {});
  }

  /**
   * Removing the LAST pack while an Extra Pack is in the cart: take both
   * out in one /cart/update.js call so the pack never flashes at $29.99.
   * Capture phase so Dawn's remove handler and the bundle remover do not
   * run; only intercepts when a pouch line is actually in the drawer.
   */
  // Extra Pack = the unlisted "extra-pack" product (2026-09-13); the marker
  // property is kept as a fallback for lines added before the switch.
  function isPouchItem(item) {
    return item.handle === 'extra-pack' || !!(item.properties && item.properties._bb_extra_pouch);
  }
  function isAnchorItem(item) {
    if (isPouchItem(item)) return false;
    if (item.handle === 'boochbod-probiotic-kombucha-gummies') return true;
    var props = item.properties || {};
    return Object.keys(props).some(function (k) { return k.charAt(0) === '_'; });
  }

  document.addEventListener('click', function (event) {
    var removeBtn = event.target.closest('cart-remove-button button, .cart-bundle-remove');
    if (!removeBtn) return;
    var row = removeBtn.closest('.cart-item');
    if (row && row.classList.contains('cart-item--pouch')) return;      // removing the pouch itself: Dawn handles it
    if (!document.querySelector('#CartDrawer .cart-item--pouch')) return; // no pouch in the cart: nothing to do
    event.preventDefault();
    event.stopPropagation();
    if (busy) return;
    setBusy(true);
    var wrapper = removeBtn.closest('cart-remove-button');
    var index = wrapper ? parseInt(wrapper.getAttribute('data-index'), 10) : NaN;
    var bundleKeys = removeBtn.getAttribute('data-bundle-keys');
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var removeKeys = {};
        if (bundleKeys) {
          bundleKeys.split(',').forEach(function (k) { if (k) removeKeys[k] = 0; });
        } else if (!isNaN(index) && cart.items[index - 1]) {
          removeKeys[cart.items[index - 1].key] = 0;
        }
        var anchorsLeft = cart.items.filter(function (i) { return !(i.key in removeKeys) && isAnchorItem(i); });
        if (!anchorsLeft.length) {
          cart.items.forEach(function (i) { if (isPouchItem(i)) removeKeys[i.key] = 0; });
        }
        return fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: removeKeys })
        });
      })
      .then(refresh)
      .catch(function (err) { console.error('[BoochBod cart] remove failed', err); })
      .finally(function () { setBusy(false); });
  }, true);

  // Extra pouch lines carry a marker property so the drawer can tell them
  // from Mix & Match bundle children (which also use "_" properties), and
  // so removeOrphanPouches() can find them. Always one-time: no selling_plan.
  function addVariant(id, isPouch) {
    var payload = { items: [{ id: id, quantity: 1 }] };
    if (isPouch) payload.items[0].properties = { _bb_extra_pouch: '1' };
    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    });
  }

  /**
   * The pouch only makes sense next to a pack. After every drawer refresh,
   * if the rendered drawer says no anchor is present, drop any pouch lines.
   */
  function removeOrphanPouches() {
    var form = document.getElementById('CartDrawer-Form');
    if (!form || form.getAttribute('data-bb-anchor-present') !== 'false') return;
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var keys = cart.items.filter(isPouchItem).map(function (i) { return i.key; });
        if (!keys.length) return;
        return keys.reduce(function (chain, key) {
          return chain.then(function () {
            return fetch(window.Shopify.routes.root + 'cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 })
            });
          });
        }, Promise.resolve()).then(refresh);
      })
      .catch(function () {});
  }
  // Throttled, not debounced: third-party scripts mutate the page
  // continuously, so a resetting timer would never fire. Scoped to the
  // drawer so page-wide noise does not trigger cart fetches.
  var orphanTarget = document.querySelector('cart-drawer') || document.documentElement;
  new MutationObserver(function () {
    if (removeOrphanPouches._t) return;
    removeOrphanPouches._t = setTimeout(function () {
      removeOrphanPouches._t = null;
      removeOrphanPouches();
    }, 500);
  }).observe(orphanTarget, { childList: true, subtree: true });
  // ...and once on load: a cart that arrives pouch-only (pack removed on a
  // previous page, or on the /cart page) produces no mutation to react to.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(removeOrphanPouches, 800); });
  } else {
    setTimeout(removeOrphanPouches, 800);
  }

  function addVariantLegacy(id) {
    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed: ' + r.status);
      return r.json();
    });
  }

  function removeVariant(id) {
    // /cart/change.js keyed by variant id sets that line to 0.
    return fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: String(id), quantity: 0 })
    }).then(function (r) {
      if (!r.ok) throw new Error('remove failed: ' + r.status);
      return r.json();
    });
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-bb-add-variant]');
    if (!btn || busy) return;

    event.preventDefault();
    setBusy(true);

    addVariant(btn.getAttribute('data-bb-add-variant'), btn.hasAttribute('data-bb-pouch'))
      .then(refresh)
      .catch(function (err) {
        console.error('[BoochBod cart] extra pack add failed', err);
      })
      .finally(function () {
        setBusy(false);
      });
  });

  // Extra pouch: the checkbox only reveals the flavour pills (Figma layout,
  // 2026-09-13). The pill click is what adds the variant, below.
  // AutoShip & Save button on a one-time gummies line: re-add the same
  // variant with the pack's plan, then drop the one-time line.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-bb-autoship]');
    if (!btn || busy) return;
    event.preventDefault();
    setBusy(true);
    var key = btn.getAttribute('data-item-key');
    var plan = btn.getAttribute('data-selling-plan');
    var qty = parseInt(btn.getAttribute('data-quantity'), 10) || 1;
    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty, selling_plan: plan })
    })
      .then(function (r) { if (!r.ok) throw new Error('change failed'); return r.json(); })
      .then(refresh)
      .catch(function (err) { console.error('[BoochBod cart] autoship convert failed', err); })
      .finally(function () { setBusy(false); });
  });

  // AutoShip & Save on a ONE-TIME Mix & Match bundle (Walter, 17 Sep 2026):
  // every child line is re-tagged with the SUBSCRIPTION bundle's id and given
  // the pack's plan, one /cart/change.js per line. Appstle's discount function
  // then applies the subscription bundle's discount. See cart-drawer.liquid.
  // GOTCHA: changing one line re-keys EVERY line in the cart, so keys taken
  // up front go stale after the first change ("no valid id or line parameter").
  // Each pass re-reads the cart and converts the next line still carrying the
  // one-time bundle id (data-bb-from) without a plan.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-bb-autoship-bundle]');
    if (!btn || busy) return;
    event.preventDefault();
    setBusy(true);
    var plan = btn.getAttribute('data-selling-plan');
    var fromId = btn.getAttribute('data-bb-from');
    var bbId = btn.getAttribute('data-bb-id');
    var bbName = btn.getAttribute('data-bb-name');
    var root = window.Shopify.routes.root;
    var guard = 0;

    function next() {
      if (++guard > 12) return Promise.resolve();
      return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var item = null;
          for (var i = 0; i < cart.items.length; i++) {
            var it = cart.items[i];
            var p = it.properties || {};
            if (p['_appstle-bb-id'] === fromId && !it.selling_plan_allocation) { item = it; break; }
          }
          if (!item) return;
          var props = {};
          Object.keys(item.properties || {}).forEach(function (k) { props[k] = item.properties[k]; });
          props['_appstle-bb-id'] = bbId;
          props['__appstle-bb-name'] = bbName;
          props['_appstle_bundles_subscription_selling_plan_id'] = Number(plan);
          return fetch(root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: item.quantity, selling_plan: plan, properties: props })
          })
            .then(function (r) { if (!r.ok) throw new Error('change failed'); return r.json(); })
            .then(next);
        });
    }

    next()
      .then(refresh)
      .catch(function (err) { console.error('[BoochBod cart] bundle autoship convert failed', err); refresh(); })
      .finally(function () { setBusy(false); });
  });

  // CAPTURE phase + stopPropagation on both upsell checkboxes: Dawn's
  // <cart-drawer-items> listens for any 'change' in the drawer and runs
  // validateQuantity(), which reads a numeric step from the checkbox and shows
  // "You can only add this item in increments of".
  document.addEventListener('change', function (event) {
    var t = event.target;
    if (!t || !t.matches('[data-bb-pack-toggle]')) return;
    event.stopPropagation(); // keep Dawn's validateQuantity() off our checkbox
    var key = t.getAttribute('data-bb-pouch-key');
    if (!t.checked && key) {
      // pouch already in the cart: unticking removes it
      setBusy(true);
      fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      }).then(refresh).catch(function () {}).finally(function () { setBusy(false); });
      return;
    }
    var box = t.closest('[data-bb-upsell-pack]');
    var fl = box && box.querySelector('[data-bb-pack-flavours]');
    if (fl) fl.hidden = !t.checked;
  }, true);

  document.addEventListener('change', function (event) {
    var box = event.target.closest('[data-bb-protect-variant]');
    if (!box || busy) return;
    event.stopPropagation(); // see above

    var id = box.getAttribute('data-bb-protect-variant');
    var wanted = box.checked;

    setBusy(true);

    (wanted ? addVariant(id) : removeVariant(id))
      .then(refresh)
      .catch(function (err) {
        console.error('[BoochBod cart] protection toggle failed', err);
        // Put the checkbox back so it cannot claim a state the cart does not
        // actually have.
        box.checked = !wanted;
      })
      .finally(function () {
        setBusy(false);
      });
  }, true);
})();
